import type { ModelBounds } from "../../scene/WarehouseModel";
import type { Bbox } from "../planRaster";

/**
 * ADR 310 — the Modelo tab's workbench sized from the model it is calibrating.
 *
 * The workbench was authored against the 60 × 40 m demo warehouse: fixed grid,
 * camera at 30–40 m, offset sliders clamped to ±50 m. The first real CAD export
 * (543 × 495 m, authored centred on Blender's origin) dwarfed the grid, started
 * the camera inside the building, and needed a ~250 m offset the sliders could
 * not express — the tool looked broken precisely when it was needed most.
 *
 * Everything here derives from one measurement of the UNTRANSFORMED GLB, taken
 * once per model load. The frame deliberately does not chase the placement
 * sliders: a workbench that re-fits while the operator drags is a treadmill.
 */
export interface WorkbenchFrame {
  /** Grid edge, metres — also the reference floor the model is judged against. */
  floor: number;
  gridDivisions: number;
  camera: [number, number, number];
  /** Orbit target: the model's footprint centre at floor height. */
  target: [number, number, number];
  /** Offset sliders span ±this, metres. */
  offsetRange: number;
  offsetStep: number;
  /** Camera far plane — the drei default (2000) clips a 500 m model mid-orbit. */
  far: number;
}

/** The pre-ADR-310 workbench, still the floor for small models and the frame
 *  shown until the GLB has loaded and measured. */
const LEGACY: WorkbenchFrame = {
  floor: 60,
  gridDivisions: 60,
  camera: [30, 40, 30],
  target: [0, 0, 0],
  offsetRange: 50,
  offsetStep: 0.1,
  far: 2000,
};

/** Round up to a full 10 m so grid cells and slider ends land on round numbers. */
const nice = (v: number) => Math.ceil(v / 10) * 10;

/**
 * @param b       the GLB's untransformed envelope, once measured
 * @param content 2D bbox of the imported structure (x, y — y IS scene z, ADR 25):
 *                when present the frame covers model AND structure together, so
 *                the reference the operator aligns onto is on screen before the
 *                model has been moved anywhere near it (ADR 312).
 */
export function frameFor(b: ModelBounds | null, content: Bbox | null = null): WorkbenchFrame {
  if (!b && !content) return LEGACY;
  const minX = Math.min(b ? b.center[0] - b.size[0] / 2 : Infinity, content ? content.minX : Infinity);
  const maxX = Math.max(b ? b.center[0] + b.size[0] / 2 : -Infinity, content ? content.maxX : -Infinity);
  const minZ = Math.min(b ? b.center[2] - b.size[2] / 2 : Infinity, content ? content.minY : Infinity);
  const maxZ = Math.max(b ? b.center[2] + b.size[2] / 2 : -Infinity, content ? content.maxY : -Infinity);
  const cx = (minX + maxX) / 2, cz = (minZ + maxZ) / 2;
  const extent = Math.max(maxX - minX, maxZ - minZ, 1);
  const floor = Math.max(LEGACY.floor, nice(extent * 1.15));
  // Enough range to move the model across its own extent, or back to the origin
  // from wherever it was authored — whichever is farther.
  const offsetRange = Math.max(
    LEGACY.offsetRange,
    nice(Math.abs(cx) + extent),
    nice(Math.abs(cz) + extent),
  );
  return {
    floor,
    // 1 m cells while they are legible; 10 m cells once they would be a moiré.
    gridDivisions: floor <= 100 ? floor : floor / 10,
    camera: [cx + 0.55 * floor, 0.7 * floor, cz + 0.55 * floor],
    target: [cx, 0, cz],
    offsetRange,
    offsetStep: offsetRange > 100 ? 0.5 : 0.1,
    far: Math.max(LEGACY.far, floor * 8),
  };
}
